import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, Plug, Loader2, Eye, EyeOff, Clock, ShieldAlert, Save } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { api, errMsg } from "../../lib/api";

/* Phase 199 — Super Admin → Integrations: third-party API keys per municipality for the Smart Parking module.
   Secrets come back masked (••••1234); leaving a masked value untouched keeps the stored secret, clearing the field removes it.
   Every service card has a real "Test Connection". Unconfigured optional services show a Coming Soon badge. */

const SERVICE_ORDER = ["payment", "fastag", "vaahan", "sms", "whatsapp", "firebase"];

function TestResult({ r }) {
  if (!r) return null;
  const when = r.at ? new Date(r.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "";
  if (r.status === "not_configured") {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="integration-test-result">
        <ShieldAlert className="h-3.5 w-3.5 text-[hsl(var(--warning))]" /> {r.message}
      </p>
    );
  }
  return (
    <p className={`text-xs flex items-start gap-1.5 ${r.ok ? "text-[hsl(var(--success))]" : "text-destructive"}`} data-testid="integration-test-result" data-ok={r.ok ? "true" : "false"}>
      {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
      <span>{r.message}{when ? <span className="text-muted-foreground"> · tested {when}{r.ms != null ? ` · ${r.ms} ms` : ""}</span> : null}</span>
    </p>
  );
}

function ServiceCard({ svcKey, svc, values, onChange, onSave, onTest, saving, testing, dirty }) {
  const [reveal, setReveal] = useState({});
  const statusBadge = svc.coming_soon
    ? <Badge variant="outline" className="text-[10px] border-[hsl(var(--warning))] text-[hsl(var(--warning))]" data-testid={`integration-${svcKey}-coming-soon`}><Clock className="h-3 w-3 mr-1" /> Coming Soon</Badge>
    : svc.configured
      ? <Badge variant="outline" className="text-[10px] border-[hsl(var(--success))] text-[hsl(var(--success))]" data-testid={`integration-${svcKey}-configured`}>Configured</Badge>
      : <Badge variant="secondary" className="text-[10px]" data-testid={`integration-${svcKey}-not-configured`}>Not configured</Badge>;
  return (
    <Card className="shadow-sm" data-testid={`integration-card-${svcKey}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2"><Plug className="h-4 w-4 text-primary" /> {svc.label}
              {svc.optional && <span className="text-[11px] font-normal text-muted-foreground">(optional)</span>}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">{svc.help}</p>
          </div>
          {statusBadge}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {svc.fields.map((f) => {
            const v = values[f.key] ?? "";
            const id = `integration-${svcKey}-${f.key}`;
            if (f.type === "select") {
              return (
                <div key={f.key} className="space-y-1">
                  <Label htmlFor={id} className="text-xs">{f.label}</Label>
                  <Select value={v || f.default} onValueChange={(nv) => onChange(f.key, nv)}>
                    <SelectTrigger id={id} data-testid={id}><SelectValue /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              );
            }
            const isSecret = f.type === "secret";
            return (
              <div key={f.key} className="space-y-1">
                <Label htmlFor={id} className="text-xs">{f.label}</Label>
                <div className="relative">
                  <Input id={id} data-testid={id} value={v} type={isSecret && !reveal[f.key] ? "password" : f.type === "number" ? "number" : "text"}
                    min={f.min} max={f.max} placeholder={f.type === "url" ? "https://" : ""} autoComplete="off"
                    onChange={(e) => onChange(f.key, e.target.value)} className={isSecret ? "pr-9" : ""} />
                  {isSecret && (
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setReveal((r) => ({ ...r, [f.key]: !r[f.key] }))} aria-label={reveal[f.key] ? "Hide" : "Show"} data-testid={`${id}-toggle`}>
                      {reveal[f.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                {f.help && <p className="text-[11px] text-muted-foreground">{f.help}</p>}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <TestResult r={svc.last_test} />
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={onTest} disabled={testing || dirty} title={dirty ? "Save first, then test" : "Send a real test request"}
              data-testid={`integration-${svcKey}-test`}>
              {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plug className="h-4 w-4 mr-1" />} Test Connection
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving || !dirty} data-testid={`integration-${svcKey}-save`}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Integrations() {
  const [munis, setMunis] = useState(null);
  const [muniId, setMuniId] = useState("");
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});

  useEffect(() => {
    api.get("/platform/parking/activations").then((r) => {
      const list = r.data.municipalities || [];
      setMunis(list);
      const first = list.find((m) => m.smart_parking_active) || list[0];
      if (first) setMuniId(first.id);
    }).catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);

  const load = async (id) => {
    if (!id) return;
    setData(null);
    try {
      const r = await api.get(`/platform/integrations/${id}`);
      setData(r.data);
      setDraft(Object.fromEntries(Object.entries(r.data.services).map(([k, s]) => [k, { ...s.values }])));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(muniId); }, [muniId]);

  const isDirty = (k) => JSON.stringify(draft[k] || {}) !== JSON.stringify(data?.services?.[k]?.values || {});

  const save = async (k) => {
    setSaving((s) => ({ ...s, [k]: true }));
    try {
      const r = await api.put(`/platform/integrations/${muniId}`, { services: { [k]: draft[k] } });
      setData((d) => ({ ...d, ...r.data }));
      setDraft((dr) => ({ ...dr, [k]: { ...r.data.services[k].values } }));
      toast.success(`${data.services[k].label} saved`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving((s) => ({ ...s, [k]: false })); }
  };

  const test = async (k) => {
    setTesting((s) => ({ ...s, [k]: true }));
    try {
      const r = await api.post(`/platform/integrations/${muniId}/test/${k}`);
      setData((d) => ({ ...d, services: { ...d.services, [k]: { ...d.services[k], last_test: r.data } } }));
      if (r.data.ok) toast.success(r.data.message); else if (r.data.status === "not_configured") toast.info(r.data.message); else toast.error(r.data.message);
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting((s) => ({ ...s, [k]: false })); }
  };

  const muni = useMemo(() => (munis || []).find((m) => m.id === muniId), [munis, muniId]);
  if (munis === null) return <Spinner />;

  return (
    <div data-testid="integrations-page">
      <PageHeader title="Integrations" subtitle="Third-party API keys for the Smart Parking module — per municipality, with live connection tests">
        <div className="min-w-[260px]">
          <Select value={muniId} onValueChange={setMuniId}>
            <SelectTrigger data-testid="integrations-muni-select"><SelectValue placeholder="Select municipality" /></SelectTrigger>
            <SelectContent>
              {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.smart_parking_active ? "" : " (Smart Parking not assigned)"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      {muni && !muni.smart_parking_active && (
        <Alert className="mb-4" data-testid="integrations-inactive-alert">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Smart Parking is not assigned to {muni.name}</AlertTitle>
          <AlertDescription>Keys can be saved now; they take effect once the service is assigned from Master Services → Assign to Municipality.</AlertDescription>
        </Alert>
      )}
      <Alert className="mb-4 bg-accent/40 border-accent">
        <Plug className="h-4 w-4" />
        <AlertTitle>Nothing breaks without keys</AlertTitle>
        <AlertDescription>Unconfigured services fall back gracefully — payments run in simulated mode, SMS / WhatsApp / push alerts are logged in-app, FASTag and Vaahan show "Coming Soon" to citizens.</AlertDescription>
      </Alert>

      {!data ? <Spinner /> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {SERVICE_ORDER.filter((k) => data.services[k]).map((k) => (
            <ServiceCard key={k} svcKey={k} svc={data.services[k]} values={draft[k] || {}}
              onChange={(f, v) => setDraft((d) => ({ ...d, [k]: { ...(d[k] || {}), [f]: v } }))}
              onSave={() => save(k)} onTest={() => test(k)} saving={!!saving[k]} testing={!!testing[k]} dirty={isDirty(k)} />
          ))}
        </div>
      )}
      {data?.updated_at && <p className="text-xs text-muted-foreground mt-4" data-testid="integrations-updated">Last saved {new Date(data.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}{data.updated_by_name ? ` by ${data.updated_by_name}` : ""}</p>}
    </div>
  );
}
